import { useEffect, ReactNode, useRef } from 'react';
import { Outlet } from 'react-router';
import { TokenAutoRefresh } from 'src/components/TokenAutoRefresh';
import { SessionExpirationWarning } from 'src/components/SessionExpirationWarning';
import { useAuth, useAuthProvider } from 'src/auth/useAuth';
import { isAuthDisabled, isKeycloakAuthDisabled } from 'src/auth/authConfig';

interface AuthProtectedRouteProps {
  children?: ReactNode;
}

/**
 * Unified Authentication Protected Route Component
 *
 * This component provides authentication protection for routes using either
 * Keycloak or Microsoft Entra ID authentication providers. It maintains 100%
 * backward compatibility with the existing KeycloakProtectedRoute component
 * while adding support for the unified authentication system.
 *
 * Features:
 * - Automatic provider detection based on AUTH_PROVIDER environment variable
 * - Same initialization and authentication flow as KeycloakProtectedRoute
 * - React strict mode compatibility fixes
 * - Token refresh popup integration
 * - Authentication bypass support (VITE_DISABLE_KEYCLOAK_AUTH)
 *
 * The component automatically:
 * 1. Initializes the configured authentication provider
 * 2. Redirects to login if user is not authenticated
 * 3. Renders children/outlet when user is authenticated
 * 4. Shows token refresh popup when needed
 */
const AuthProtectedRoute = ({ children }: AuthProtectedRouteProps) => {
  const _strictModeFixInitialized = useRef(false);
  const _strictModeFixAuthenticated = useRef(false);
  const { initialized, actions, authenticated } = useAuth();
  const authProvider = useAuthProvider();

  // Check if authentication is disabled
  const globalAuthDisabled = isAuthDisabled();
  const keycloakSpecificDisabled =
    authProvider === 'keycloak' && isKeycloakAuthDisabled();
  const authDisabled = globalAuthDisabled || keycloakSpecificDisabled;

  // Initialize authentication provider
  useEffect(() => {
    // Skip initialization if authentication is disabled
    if (authDisabled) return;

    // Skip if already initialized
    if (initialized) return;

    // React strict mode fix - prevent double initialization
    if (_strictModeFixInitialized.current) return;
    _strictModeFixInitialized.current = true;

    // Initialize the authentication provider
    actions.initialize().catch((error) => {
      console.error(
        `Failed to initialize ${authProvider} authentication:`,
        error,
      );
    });
  }, [initialized, actions, authProvider, authDisabled]);

  // Handle login when not authenticated
  useEffect(() => {
    // Skip login if authentication is disabled
    if (authDisabled) return;

    // Wait for initialization to complete
    if (!initialized) return;

    // Skip if already authenticated
    if (authenticated) return;

    // React strict mode fix - prevent double login attempts
    if (_strictModeFixAuthenticated.current) return;
    _strictModeFixAuthenticated.current = true;

    // Trigger login flow
    actions.login().catch((error) => {
      console.error(`Failed to login with ${authProvider}:`, error);
    });
  }, [initialized, authenticated, actions, authProvider, authDisabled]);

  // Don't render children if authentication is required but user is not authenticated
  if (!authenticated && !authDisabled) {
    return null;
  }

  // Render children or outlet with the backend session expiration warning only.
  return (
    <>
      {children ?? <Outlet />}
      <TokenAutoRefresh />
      <SessionExpirationWarning />
    </>
  );
};

export default AuthProtectedRoute;
